import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

interface PerformanceReview {
  id: string;
  periodLabel: string;
  reviewDate: string;
  currentRoleProgress: string | null;
  futureOpportunity: string | null;
  reviewerId: string | null;
  reviewerName: string | null;
}

/** "YYYY-MM-DD" parses as UTC — build the Date from parts to avoid a one-day-back shift in US timezones. */
function formatReviewDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function PerformanceReviewsTab({ ownerId }: { ownerId: string }) {
  const [reviews, setReviews] = useState<PerformanceReview[] | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [sessionOwnerId, setSessionOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [periodLabel, setPeriodLabel] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [currentRoleProgress, setCurrentRoleProgress] = useState("");
  const [futureOpportunity, setFutureOpportunity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchReviews = useCallback(async () => {
    const res = await fetch(`/api/owners/${ownerId}/performance-reviews`);
    if (res.ok) {
      const data = await res.json();
      setReviews(data.reviews);
      setCanWrite(data.canWrite);
      setSessionOwnerId(data.sessionOwnerId);
    }
  }, [ownerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchReviews();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchReviews]);

  async function submitReview() {
    setFormError(null);
    if (!periodLabel.trim() || !reviewDate) {
      setFormError("A period label and review date are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/performance-reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          periodLabel: periodLabel.trim(),
          reviewDate,
          currentRoleProgress: currentRoleProgress.trim() || undefined,
          futureOpportunity: futureOpportunity.trim() || undefined,
        }),
      });
      if (res.ok) {
        setPeriodLabel("");
        setReviewDate("");
        setCurrentRoleProgress("");
        setFutureOpportunity("");
        setShowForm(false);
        await fetchReviews();
      } else {
        const data = await res.json().catch(() => null);
        setFormError(data?.error ?? "Could not save review.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteReview(id: string) {
    await fetch(`/api/performance-reviews/${id}`, { method: "DELETE" });
    await fetchReviews();
  }

  if (loading) {
    return (
      <div className="max-w-2xl space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      {canWrite && (
        <div className="space-y-3">
          {!showForm ? (
            <Button size="sm" onClick={() => setShowForm(true)}>
              New Review
            </Button>
          ) : (
            <div className="space-y-3 rounded-md border p-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Period</Label>
                  <Input
                    placeholder="e.g. Q2 FY26/27"
                    value={periodLabel}
                    onChange={(e) => setPeriodLabel(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Review Date</Label>
                  <Input
                    type="date"
                    value={reviewDate}
                    onChange={(e) => setReviewDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Current Role Progress</Label>
                <Textarea
                  value={currentRoleProgress}
                  onChange={(e) => setCurrentRoleProgress(e.target.value)}
                  rows={3}
                  placeholder="How are they doing in their current role?"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Future Opportunity</Label>
                <Textarea
                  value={futureOpportunity}
                  onChange={(e) => setFutureOpportunity(e.target.value)}
                  rows={3}
                  placeholder="Growth path, promotion readiness, next steps"
                />
              </div>
              {formError && <p className="text-xs text-destructive">{formError}</p>}
              <div className="flex gap-2">
                <Button size="sm" onClick={submitReview} disabled={submitting}>
                  {submitting ? "Saving…" : "Save Review"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setFormError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!reviews || reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground">No performance reviews recorded yet.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <Card key={r.id}>
              <CardContent className="space-y-2 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-heading text-base font-semibold">{r.periodLabel}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatReviewDate(r.reviewDate)}
                      {r.reviewerName && ` — reviewed by ${r.reviewerName}`}
                    </p>
                  </div>
                  {(r.reviewerId === sessionOwnerId || canWrite) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs text-destructive"
                      onClick={() => deleteReview(r.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
                {r.currentRoleProgress && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Current Role Progress
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{r.currentRoleProgress}</p>
                  </div>
                )}
                {r.futureOpportunity && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Future Opportunity
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{r.futureOpportunity}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
